import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import AuthorizationTokenStorage from '../auth/AuthorizationTokenStorage';
import { EventHelperTypes } from '../exports/Constants';
import { store } from '../../store';
import HelperMethods from '../exports/HelperMethods';
import ApiMockUtility from '../utilities/ApiMockUtility';
import { RenderableErrorResponse, ReservedHttpResponseCodes, Response } from '../exports/Types';
import { ErrorHandlerInterface } from '../exports/Interfaces';
import { collect } from 'collect.js';
import GlobalAppUtility from '../utilities/GlobalAppUtility';
import i18n from 'i18next';

export const PREFERRED_LOCALE_HEADER = 'x-preferred-locale';

class BackendApiClient {
  private client: AxiosInstance;
  private authToken = null;

  private translationPath = 'Services:Api:BackendApiClient';
  private fallbackLocale = 'en';

  constructor() {
    this.client = axios.create({
      baseURL: this.getApiBaseUrl(),
      headers: {
        Authorization: `Bearer ${this.authToken}`,
        [PREFERRED_LOCALE_HEADER]: i18n.language ?? this.fallbackLocale,
      },
    });
  }

  public async request(
    configuration: AxiosRequestConfig,
    test?: boolean,
    errorHandlers?: ErrorHandlerInterface,
  ): Promise<any> {
    if (await this.getAuthToken()) {
      if (test) {
        return new ApiMockUtility(configuration).requestAuthorized();
      }

      return this.requestAuthorized(configuration, errorHandlers);
    }

    if (test) {
      return new ApiMockUtility(configuration).request();
    }

    return this.requestAsync(configuration, errorHandlers);
  }

  async requestAsync(configuration: AxiosRequestConfig, errorHandlers?: ErrorHandlerInterface): Promise<any> {
    try {
      const response = await this.client.request(configuration);

      return {
        success: true,
        response,
        status: response.status,
      };
    } catch (error) {
      this.errorHandler(error, errorHandlers);

      return {
        success: false,
        response: error,
        status: error?.response?.status,
      };
    }
  }

  async requestAuthorized(
    configuration: AxiosRequestConfig,
    errorHandlers?: ErrorHandlerInterface,
  ): Promise<Response<any>> {
    const newConfig = await this.createRequestConfig({
      ...configuration,
      headers: {
        [PREFERRED_LOCALE_HEADER]: i18n.language ?? this.fallbackLocale,
        ...configuration.headers,
      },
    });

    if (!newConfig?.headers?.Authorization.split('Bearer ')[1]) {
      return {
        success: false,
        response: null,
        status: 401,
      };
    }

    try {
      const response = await this.client.request(newConfig);

      return {
        success: true,
        response,
        status: response.status,
      };
    } catch (error) {
      this.errorHandler(error, errorHandlers);

      return {
        success: false,
        response: error,
        status: error?.response?.status,
      };
    }
  }

  private errorHandler(error: RenderableErrorResponse, errorHandlers?: ErrorHandlerInterface) {
    if (!error.response?.status) {
      return;
    }

    const handler =
      {
        0: () => this.dispatchNoInternet(),
        401: () => this.handleInvalidToken(),
        [ReservedHttpResponseCodes.ErrorResponseModal]: () => {
          const onConfirm = collect(errorHandlers).keys().contains(error.response.data.error_code)
            ? errorHandlers[error?.response?.data?.error_code]
            : null;

          return GlobalAppUtility.globalAppMethodsContext?.toggleInfoModal({
            openModal: true,
            title: error?.response?.data?.title,
            description: error?.response?.data?.description,
            primaryButtonTitle: i18n.t(`${this.translationPath}.info_modal.button_title`),
            onConfirm,
          });
        },
      }[error.response.status] ?? (() => null);

    return handler();
  }

  async createRequestConfig(configuration: AxiosRequestConfig): Promise<AxiosRequestConfig> {
    const token = await this.getAuthToken();

    return {
      ...configuration,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        [PREFERRED_LOCALE_HEADER]: i18n.language ?? this.fallbackLocale,
        ...configuration?.headers,
      },
    };
  }

  public async handleInvalidToken() {
    if (store.getState().eventHelper.event.type || !(await this.getAuthToken())) {
      return;
    }

    HelperMethods.triggerEventSideEffects({
      type: EventHelperTypes.LOG_OUT,
      payload: null,
    });
  }

  private getApiBaseUrl(): string | undefined {
    return import.meta.env.VITE_API_BASE_URL;
  }

  async getAuthToken(): Promise<string> {
    const { authToken } = this;

    if (authToken) {
      return authToken;
    }

    return AuthorizationTokenStorage.getToken() ?? '';
  }

  public deleteAuthToken(): void {
    this.authToken = null;
  }

  public setFallbackLocale(value: string): void {
    this.fallbackLocale = value;
  }

  public dispatchNoInternet(): void {
    HelperMethods.triggerEventSideEffects({
      type: EventHelperTypes.NO_INTERNET,
      payload: null,
    });
  }
}

export default new BackendApiClient();
